/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * Right-hand "Ask Rovo" prompt slot for the Post Stream Review toolbar.
 *
 * Mirrors the Rovo / fallback prompt presentation from `Prompt.tsx`:
 *   - If the product has a Rovo `onSubmit` hook AND the modal config
 *     requests `openInRovoChat`, use `RovoPromptScreenWithLogic` (Rovo Chat).
 *   - Otherwise if the product provides a `PromptEditor`, use the fallback
 *     `PromptScreen` with the local free-gen text input.
 *   - Otherwise render `RovoPromptScreenWithLogic` with no Rovo Chat wiring.
 *
 * The frameless `rovoInlineChatAppearance` strips the prompt's own dialog
 * chrome (padding, border, shadow, trailing submit button) since the
 * outer toolbar already provides all of those affordances.
 */
import { css, jsx } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';
import { PromptScreen } from '@atlassian/generative-ai-modal/screens/Prompt';
import type { PromptEditorProps } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';

import { RovoPromptScreenWithLogic } from '../../../ExperienceApplication/screens-with-logic/RovoPromptScreenWithLogic';

/**
 * Wrapper around the embedded `RovoInlineChatPrompt` (frameless appearance).
 * Provides the subtle 1px outline + small horizontal padding so the editable
 * region reads as a distinct input field within the toolbar rather than
 * blending into the surrounding chrome.
 */
const askRovoCSS = css({
	flex: 1,
	minWidth: 0,
	display: 'flex',
	alignItems: 'center',
	border: `${token('border.width')} solid ${token('color.border.input')}`,
	borderRadius: token('radius.small'),
	paddingInline: token('space.075'),
});

export interface AskRovoProps {
	hasRovoPrompt: boolean;
	hasFallbackPrompt: boolean;
	openInRovoChat: boolean | undefined;
	openInRovoChatConfig: { onBeforeSubmit: (prompt: DocNode) => boolean };
	cancel: (_options?: {
		shouldClearSelectionContext?: boolean;
		shouldIgnoreNextSpaceTrigger?: boolean;
	}) => void;
	PromptEditor: ((props: PromptEditorProps) => JSX.Element) | undefined;
	textInput: string;
	onTextChange: (input: string) => void;
	submit: () => void;
	appearance: EditorAppearance;
	isAIFC: boolean | undefined;
}

export function AskRovo({
	hasRovoPrompt,
	hasFallbackPrompt,
	openInRovoChat,
	openInRovoChatConfig,
	cancel,
	PromptEditor,
	textInput,
	onTextChange,
	submit,
	appearance,
	isAIFC,
}: AskRovoProps): JSX.Element {
	return (
		<div css={askRovoCSS} data-testid="post-stream-review-ask-rovo">
			{openInRovoChat && hasRovoPrompt ? (
				<RovoPromptScreenWithLogic
					cancel={cancel}
					openInRovoChatConfig={openInRovoChatConfig}
					autoClose={false}
					rovoInlineChatAppearance="frameless"
				/>
			) : hasFallbackPrompt && PromptEditor ? (
				<PromptScreen
					textInput={textInput}
					onTextChange={onTextChange}
					submit={submit}
					cancel={cancel}
					PromptEditor={PromptEditor}
					appearance={appearance}
					isAIFC={isAIFC}
				/>
			) : (
				<RovoPromptScreenWithLogic cancel={cancel} />
			)}
		</div>
	);
}
